/**
 * EmailSequenceBuilder Modal
 * 
 * Generates a 5-email follow-up sequence based on user's business.
 * Output varies by plan tier:
 * - Starter: Email copy + DIY instructions
 * - Growth: Editable templates with placeholders
 * - Pro: Done-for-you finished sequence ready to download
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MagicWand, Sparkle, Lightning, CheckCircle, Copy, Download, 
  ArrowRight, Envelope, EnvelopeSimple, PaperPlaneTilt,
  Spinner, Info, Eye, PencilSimple, CaretDown, CaretUp, ArrowUpRight, Lock
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { isTrialUser, canDownload, getUpgradeMessage, getEffectiveTier } from '@/utils/trialAccess';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import useBusinessMemoryPrefill from '@/hooks/useBusinessMemoryPrefill';
import useFoundationAutoStart from '@/hooks/useFoundationAutoStart';
import useFoundationAutoRoute from '@/hooks/useFoundationAutoRoute';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { normalizeBuilderAsset } from '@/utils/normalizeBuilderAsset';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import NextStepGuide from './NextStepGuide';
import EditablePreview from './EditablePreview';

const TIER_BADGES = {
  starter: { label: 'Starter', color: 'bg-slate-600', description: 'Copy + DIY Instructions' },
  growth: { label: 'Growth', color: 'bg-blue-600', description: 'Editable Templates' },
  pro: { label: 'Pro', color: 'bg-purple-600', description: 'Done-For-You + PDF' }
};

const EMAIL_TYPES = [
  { number: 1, type: 'welcome', name: 'Welcome', color: 'text-emerald-400' },
  { number: 2, type: 'problem', name: 'Problem', color: 'text-amber-400' },
  { number: 3, type: 'solution', name: 'Solution', color: 'text-blue-400' },
  { number: 4, type: 'trust', name: 'Trust', color: 'text-purple-400' },
  { number: 5, type: 'offer', name: 'Offer', color: 'text-pink-400' }
];

export default function EmailSequenceBuilder({ isOpen, onClose, onAction, onPickStep, onSuccess, user }) {
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [businessDescription, setBusinessDescription] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [offer, setOffer] = useState('');
  const [productName, setProductName] = useState('');
  const [callToAction, setCallToAction] = useState('Get Started Now');
  const [detectedBusiness, setDetectedBusiness] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Round 108 — mission handoff prefill.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id
    && (missionHandoff.asset_type === 'email_sequence'
      || missionHandoff.asset_type === 'email'
      || !missionHandoff.asset_type));

  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionDesc = pickMissionField('body', 'full_message', 'summary', 'pitch');
    if (missionDesc && typeof missionDesc === 'string') {
      setBusinessDescription(missionDesc);
    }
    const missionCustomer = pickMissionField('recommended_audience', 'target_audience');
    if (missionCustomer && typeof missionCustomer === 'string') {
      setTargetCustomer(missionCustomer);
    }
    const missionOffer = pickMissionField('pitch', 'headline', 'title', 'subject');
    if (missionOffer && typeof missionOffer === 'string') {
      setOffer(missionOffer);
    }
    const missionCta = pickMissionField('cta', 'cta_copy');
    if (missionCta && typeof missionCta === 'string') {
      setCallToAction(missionCta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);

  // Determine effective plan tier (uses simulation-aware function)
  const effectiveTier = getEffectiveTier(user);
  const tierInfo = TIER_BADGES[effectiveTier] || TIER_BADGES.starter;
  
  // Check if user is on trial (simulation-aware)
  const isTrial = isTrialUser(user);
  const canUserDownload = canDownload(user);

  // Canonical business memory prefill (pulls from /business-memory/profile which
  // now hydrates from Business Builder, Business Plans, and Offers).
  useBusinessMemoryPrefill(isOpen, {
    businessDescription: [businessDescription, setBusinessDescription],
    targetCustomer: [targetCustomer, setTargetCustomer],
    offer: [offer, setOffer],
    productName: [productName, setProductName],
  });

  // Round 218A Phase 8D-3D — auto-consume Foundation when complete.
  useFoundationAutoStart({
    isOpen,
    ready: !!(businessDescription && targetCustomer && offer && productName),
    step,
    onStart: () => handleGenerate(),
  });

  // Round 218A P0 — foundation gate.
  useFoundationAutoRoute({ isOpen, onClose });

  // Auto-detect business type as user types
  useEffect(() => {
    const detectTimeout = setTimeout(async () => {
      const combined = `${businessDescription} ${offer} ${targetCustomer} ${productName}`.trim();
      if (combined.length >= 10) {
        try {
          const response = await api.post('/email-sequence-builder/detect-business', {
            business_description: businessDescription,
            offer: offer,
            target_customer: targetCustomer,
            product_name: productName
          });
          setDetectedBusiness(response.data);
        } catch (error) {
          // Silent fail for preview
        }
      }
    }, 500);

    return () => clearTimeout(detectTimeout);
  }, [businessDescription, offer, targetCustomer, productName]);

  const handleGenerate = async () => {
    if (!businessDescription.trim()) {
      toast.error('Please describe your business');
      return;
    }
    if (!productName.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    setLoading(true);
    setStep('generating');

    try {
      const response = await api.post('/email-sequence-builder/generate', {
        business_description: businessDescription,
        target_customer: targetCustomer || 'ideal customers',
        offer: offer || productName,
        product_name: productName,
        call_to_action: callToAction || 'Get Started Now'
      });

      setResult(response.data);
      setStep('result');
      toast.success('Email sequence created!');
      try { if (onSuccess) onSuccess({ meta: normalizeBuilderAsset(response.data) }); } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate email sequence');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyAllEmails = () => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    const emails = result?.content?.emails || result?.formatted_output?.final_emails || [];
    const allText = emails.map((email, i) => {
      const subject = email.subject_line || email.subject || '';
      const preview = email.preview_text || email.preview || '';
      const body = email.body || '';
      const cta = email.cta_text || email.cta || '';
      return `--- EMAIL ${i + 1}: ${email.name || ''} ---\nSUBJECT: ${subject}\nPREVIEW: ${preview}\n\n${body}\n\n[${cta}]\n`;
    }).join('\n\n');
    navigator.clipboard.writeText(allText);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_pdf'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.id || !result?.has_pdf) {
      toast.error('PDF not available');
      return;
    }

    try {
      const response = await api.get(`/email-sequence-builder/${result.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      await safeDownloadBlob(blob, `${result.sequence_name?.replace(/[^a-z0-9]/gi, '_') || 'email_sequence'}.pdf`);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleReset = () => {
    setStep('input');
    setBusinessDescription('');
    setTargetCustomer('');
    setOffer('');
    setProductName('');
    setCallToAction('Get Started Now');
    setDetectedBusiness(null);
    setResult(null);
    setExpandedEmail(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // ──────────────────────────────────────────────────────────
  // Universal Editable Preview Mode (Phase 2) — Email Sequence
  //
  // PATCH /api/email-sequence-builder/{id}. Server whitelist drops
  // anything outside name / purpose / emails / from_name /
  // subject_lines / sender_signature / sequence_data. business_memory
  // is NEVER touched.
  // ──────────────────────────────────────────────────────────
  const [savingSequenceEdits, setSavingSequenceEdits] = useState(false);
  const editableSequence = useMemo(() => ({
    name: result?.name || result?.sequence_name || '',
    purpose: result?.purpose || '',
    from_name: result?.from_name || '',
    sender_signature: result?.sender_signature || '',
  }), [result]);

  const handleSaveSequenceEdits = useCallback(async (next) => {
    if (!result?.id) {
      toast.error('Sequence ID missing — please regenerate.');
      return;
    }
    setSavingSequenceEdits(true);
    try {
      const body = {
        name: next.name,
        purpose: next.purpose,
        from_name: next.from_name,
        sender_signature: next.sender_signature,
      };
      const { data } = await api.patch(`/email-sequence-builder/${result.id}`, body, { timeout: 30000 });
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingSequenceEdits(false);
    }
  }, [result?.id]);

  if (!isOpen) return null;

  // Get emails array from result
  const getEmails = () => {
    if (!result) return [];
    return result.content?.emails || result.formatted_output?.final_emails || result.formatted_output?.emails_copy || [];
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="email-sequence-builder-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                <EnvelopeSimple size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Email Sequence Builder</h2>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tierInfo.color} text-white`}>
                    {tierInfo.label}
                  </span>
                  <span className="text-slate-400 text-xs">{tierInfo.description}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-email-sequence-modal"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <AnimatePresence mode="wait">
              {/* INPUT STEP */}
              {step === 'input' && (
                <motion.div
                  key="input"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <BusinessMemoryBanner />
                  {/* Business Description */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      What does your business do?
                    </label>
                    <textarea
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.target.value)}
                      placeholder="e.g., I help small business owners grow their online presence..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50 resize-none"
                      rows={2}
                      data-testid="business-description-input"
                    />
                    
                    {/* Auto-detected business type */}
                    {detectedBusiness && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 flex items-center space-x-2 text-sm"
                      >
                        <Sparkle size={14} className="text-pink-400" weight="fill" />
                        <span className="text-slate-400">Detected:</span>
                        <span className="text-pink-400 font-medium">{detectedBusiness.detected_name}</span>
                      </motion.div>
                    )}
                  </div>

                  {/* Product Name */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Product/Service Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="e.g., Social Media Mastery Course"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                      data-testid="product-name-input"
                    />
                  </div>

                  {/* Target Customer */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Who is your target customer?
                    </label>
                    <input
                      type="text"
                      value={targetCustomer}
                      onChange={(e) => setTargetCustomer(e.target.value)}
                      placeholder="e.g., Small business owners who want to grow on social media"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                      data-testid="target-customer-input"
                    />
                  </div>

                  {/* Offer and CTA Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Your Offer
                      </label>
                      <input
                        type="text"
                        value={offer}
                        onChange={(e) => setOffer(e.target.value)}
                        placeholder="e.g., Free strategy call"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                        data-testid="offer-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Call to Action
                      </label>
                      <input
                        type="text"
                        value={callToAction}
                        onChange={(e) => setCallToAction(e.target.value)}
                        placeholder="e.g., Book Now"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                        data-testid="cta-input"
                      />
                    </div>
                  </div>

                  {/* Email Sequence Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Envelope size={16} className="text-pink-400" />
                      <span className="text-white text-sm font-medium">5-Email Sequence</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {EMAIL_TYPES.map((email) => (
                        <div key={email.number} className="flex items-center space-x-1.5 bg-white/5 rounded-lg px-2 py-1">
                          <span className={`text-xs font-bold ${email.color}`}>{email.number}</span>
                          <span className="text-slate-400 text-xs">{email.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tier Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Info size={16} className="text-pink-400" />
                      <span className="text-white text-sm font-medium">What you'll get ({tierInfo.label})</span>
                    </div>
                    {effectiveTier === 'starter' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Complete email copy for all 5 emails</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Step-by-step setup instructions</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Recommended email tools (Mailchimp, ConvertKit)</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'growth' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Editable templates with [customizable] sections</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Multiple subject line variations</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>CTA options for each email</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'pro' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Finished, professional email sequence</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Download as PDF</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Ready to copy/paste into any email tool</span>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerate}
                    disabled={!businessDescription.trim() || !productName.trim() || loading}
                    className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="generate-email-sequence-btn"
                  >
                    <EnvelopeSimple size={20} weight="fill" />
                    <span>Generate My Email Sequence</span>
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </motion.div>
              )}

              {/* GENERATING STEP */}
              {step === 'generating' && (
                <motion.div
                  key="generating"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Spinner size={32} className="text-white animate-spin" />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-2">Creating Your Email Sequence</h3>
                  <p className="text-slate-400 text-sm">
                    Writing 5 high-converting emails for your business...
                  </p>
                  <div className="mt-6 space-y-2 max-w-xs mx-auto">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 5 }}
                      className="h-1 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full"
                    />
                  </div>
                </motion.div>
              )}

              {/* RESULT STEP */}
              {step === 'result' && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {/* Success Header */}
                  <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle size={24} className="text-emerald-400" weight="fill" />
                      <div>
                        <h3 className="text-white font-bold">Email Sequence Created!</h3>
                        <p className="text-emerald-400 text-sm">{result.email_count || 5} emails saved to My Creations</p>
                      </div>
                    </div>
                  </div>

                  {/* Sequence Info */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <h4 className="text-white text-lg font-bold mb-1">{result.sequence_name}</h4>
                    <div className="flex items-center space-x-3 text-xs text-slate-400">
                      <span>Business: <span className="text-pink-400">{result.detected_business_name}</span></span>
                      <span>Tier: <span className="text-purple-400">{result.plan_tier}</span></span>
                    </div>
                  </div>

                  {/* Universal Editable Preview Mode — adjust sequence
                      name / from name / signature before downloading.
                      business_memory NEVER mutated. */}
                  {result.id && (
                    <EditablePreview
                      draftKey={`email-seq-draft-${result.id}`}
                      originalValue={editableSequence}
                      onSave={handleSaveSequenceEdits}
                      busy={savingSequenceEdits}
                      savedAt={result.last_edited_at || null}
                      previewSlot={({ value }) => (
                        <div className="space-y-2 text-sm text-slate-200">
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Sequence name</p>
                            <p className="text-white font-semibold">{value.name || '—'}</p>
                          </div>
                          {value.purpose && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Purpose</p>
                              <p>{value.purpose}</p>
                            </div>
                          )}
                          {value.from_name && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">From name</p>
                              <p className="text-pink-300">{value.from_name}</p>
                            </div>
                          )}
                          {value.sender_signature && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Signature</p>
                              <p className="whitespace-pre-wrap">{value.sender_signature}</p>
                            </div>
                          )}
                        </div>
                      )}
                      editSlot={({ value, setValue }) => (
                        <div className="space-y-3">
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Sequence name</span>
                            <input
                              type="text"
                              value={value.name || ''}
                              onChange={(e) => setValue({ ...value, name: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400 focus:outline-none"
                              data-testid="email-seq-edit-name"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Purpose</span>
                            <input
                              type="text"
                              value={value.purpose || ''}
                              onChange={(e) => setValue({ ...value, purpose: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400 focus:outline-none"
                              data-testid="email-seq-edit-purpose"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">From name</span>
                            <input
                              type="text"
                              value={value.from_name || ''}
                              onChange={(e) => setValue({ ...value, from_name: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400 focus:outline-none"
                              data-testid="email-seq-edit-from"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Signature</span>
                            <textarea
                              value={value.sender_signature || ''}
                              onChange={(e) => setValue({ ...value, sender_signature: e.target.value })}
                              rows={3}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400 focus:outline-none"
                              data-testid="email-seq-edit-signature"
                            />
                          </label>
                        </div>
                      )}
                    />
                  )}

                  {/* Pro Download Buttons - HIDDEN for trial users */}
                  {result.formatted_output?.output_type === 'done_for_you' && !isTrial && (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleDownloadPDF}
                        disabled={!result?.has_pdf}
                        className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="download-pdf-btn"
                      >
                        <Download size={18} weight="bold" />
                        <span>Download PDF</span>
                      </button>
                      <button
                        onClick={handleCopyAllEmails}
                        className="py-3 px-4 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                      >
                        <Copy size={16} />
                        <span>Copy All</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Trial user sees upgrade message instead of downloads */}
                  {result.formatted_output?.output_type === 'done_for_you' && isTrial && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400 font-medium mb-2">Downloads Locked</p>
                      <p className="text-slate-400 text-sm mb-3">Upgrade to Pro to download PDF and copy all emails.</p>
                      <Link 
                        to="/pricing" 
                        className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                      >
                        Upgrade Now
                      </Link>
                    </div>
                  )}

                  {/* Starter/Growth Copy All Button - HIDDEN for trial users */}
                  {result.formatted_output?.output_type !== 'done_for_you' && !isTrial && (
                    <button
                      onClick={handleCopyAllEmails}
                      className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                    >
                      <Copy size={16} />
                      <span>Copy All Emails</span>
                    </button>
                  )}
                  
                  {/* Trial user sees upgrade message instead of copy button */}
                  {result.formatted_output?.output_type !== 'done_for_you' && isTrial && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                      <p className="text-amber-400 text-sm">Upgrade to copy all emails</p>
                    </div>
                  )}

                  {/* Email List */}
                  <div className="space-y-3">
                    {getEmails().map((email, index) => {
                      const emailType = EMAIL_TYPES[index] || { number: index + 1, name: `Email ${index + 1}`, color: 'text-slate-400' };
                      const isExpanded = expandedEmail === index;
                      
                      return (
                        <div 
                          key={index}
                          className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                        >
                          {/* Email Header */}
                          <button
                            onClick={() => setExpandedEmail(isExpanded ? null : index)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <span className={`w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold ${emailType.color}`}>
                                {emailType.number}
                              </span>
                              <div className="text-left">
                                <p className="text-white text-sm font-medium">{email.name || emailType.name}</p>
                                <p className="text-slate-400 text-xs truncate max-w-[200px] sm:max-w-[300px]">
                                  {email.subject_line || email.subject || 'No subject'}
                                </p>
                              </div>
                            </div>
                            {isExpanded ? (
                              <CaretUp size={16} className="text-slate-400" />
                            ) : (
                              <CaretDown size={16} className="text-slate-400" />
                            )}
                          </button>

                          {/* Email Content (Expanded) */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-white/10"
                              >
                                <div className="p-4 space-y-3">
                                  {/* Subject */}
                                  <div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-slate-400 text-xs font-medium">SUBJECT</span>
                                      <button
                                        onClick={() => handleCopy(email.subject_line || email.subject || '')}
                                        className="p-1 hover:bg-white/10 rounded"
                                      >
                                        <Copy size={12} className="text-slate-400" />
                                      </button>
                                    </div>
                                    <p className="text-white text-sm">{email.subject_line || email.subject}</p>
                                  </div>

                                  {/* Preview */}
                                  {(email.preview_text || email.preview) && (
                                    <div>
                                      <span className="text-slate-400 text-xs font-medium">PREVIEW</span>
                                      <p className="text-slate-300 text-sm">{email.preview_text || email.preview}</p>
                                    </div>
                                  )}

                                  {/* Body */}
                                  <div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-slate-400 text-xs font-medium">BODY</span>
                                      <button
                                        onClick={() => handleCopy(email.body || '')}
                                        className="p-1 hover:bg-white/10 rounded"
                                      >
                                        <Copy size={12} className="text-slate-400" />
                                      </button>
                                    </div>
                                    <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-40 overflow-y-auto">
                                      {email.body}
                                    </pre>
                                  </div>

                                  {/* CTA */}
                                  {(email.cta_text || email.cta) && (
                                    <div className="text-center pt-2">
                                      <span className="inline-block px-4 py-2 bg-pink-600/20 text-pink-400 text-sm font-medium rounded-lg">
                                        [{email.cta_text || email.cta}]
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  {/* DIY Instructions for Starter */}
                  {result.formatted_output?.output_type === 'text_with_instructions' && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <h5 className="text-white font-medium mb-3 flex items-center space-x-2">
                        <Lightning size={16} className="text-amber-400" />
                        <span>How to Set Up Your Sequence</span>
                      </h5>
                      <ol className="space-y-2">
                        {(result.formatted_output.diy_instructions || []).slice(0, 5).map((instruction, i) => (
                          <li key={i} className="flex items-start space-x-3 text-sm">
                            <span className="w-5 h-5 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="text-slate-300">{instruction}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Upgrade Prompt for Non-Pro Users */}
                  {result?.delivery_mode !== 'done_for_you' && effectiveTier !== 'pro' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4" data-testid="upgrade-prompt">
                      <p className="text-white text-sm font-medium mb-2">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Want this finished and ready to use?'
                          : 'Want this fully built and ready to download?'
                        }
                      </p>
                      <p className="text-slate-400 text-xs mb-3">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Upgrade to Pro to get done-for-you assets.'
                          : 'Upgrade to Pro.'
                        }
                      </p>
                      <Link
                        to="/pricing"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all"
                        data-testid="upgrade-btn"
                      >
                        <span>Upgrade</span>
                        <ArrowUpRight size={16} weight="bold" />
                      </Link>
                    </div>
                  )}

                  {/* Action Buttons — Sequential workflow routing */}
                  <div className="space-y-2 pt-2">
                    <button
                      onClick={() => { onAction?.('essentials'); handleClose(); }}
                      className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                      data-testid="next-essentials-btn"
                    >
                      <span>Next: Business Essentials</span>
                      <ArrowRight size={18} />
                    </button>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleReset}
                        className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                      >
                        Create Another
                      </button>
                    </div>
                    <NextStepGuide
                      currentStep="email"
                      onPick={(stepKey) => { handleReset(); onClose(); onPickStep?.(stepKey); }}
                      onDone={handleClose}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Trial Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowUpgradePrompt(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} weight="fill" className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Upgrade to Continue</h3>
              <p className="text-slate-400 mb-6">{upgradeMessage}</p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all"
                >
                  View Plans
                </Link>
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
